/**
 * Schedules Page
 *
 * Main page for managing agent schedules. Displays a responsive grid
 * of schedule cards with glass-morphism styling. Accessible at /schedules.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  ArrowLeft,
  Loader2,
  Calendar,
  RefreshCw,
  Moon,
  Sun,
} from 'lucide-react';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import {
  AgentScheduleService,
  type Schedule,
  type RunEntry,
} from '../services/agentScheduleService';
import ScheduleCard from './ScheduleCard';
import ScheduleDetail from './ScheduleDetail';
import CreateScheduleModal from './CreateScheduleModal';
import Login from './Login';

const SchedulesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, token } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [runsMap, setRunsMap] = useState<Record<string, RunEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);

  // Derive selected schedule from the schedules array so it stays fresh after loadData
  const selectedSchedule = selectedScheduleId
    ? schedules.find((s) => s.id === selectedScheduleId) ?? null
    : null;

  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // Load schedules and recent runs
  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      const data = await AgentScheduleService.list(token);
      setSchedules(data);

      // Load last 10 runs for each schedule
      const runsPromises = data.map(async (s) => {
        try {
          const runs = await AgentScheduleService.getRuns(s.id, token, 10);
          return { id: s.id, runs };
        } catch {
          return { id: s.id, runs: [] };
        }
      });
      const runsResults = await Promise.all(runsPromises);
      const newRunsMap: Record<string, RunEntry[]> = {};
      runsResults.forEach((r) => { newRunsMap[r.id] = r.runs; });
      setRunsMap(newRunsMap);

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadData();
    }
  }, [authLoading, isAuthenticated, loadData]);

  // Poll for updates if any schedule is running
  useEffect(() => {
    const hasRunning = schedules.some((s) => s.currentRunAgentSessionId);
    if (hasRunning) {
      pollRef.current = setInterval(loadData, 8000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [schedules, loadData]);

  // Actions
  const handleRunNow = async (id: string) => {
    setActioningId(id);
    try {
      await AgentScheduleService.runNow(id, token || undefined);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to run schedule');
    } finally {
      setActioningId(null);
    }
  };

  const handleStop = async (id: string) => {
    setActioningId(id);
    try {
      await AgentScheduleService.stop(id, token || undefined);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to stop schedule');
    } finally {
      setActioningId(null);
    }
  };

  const handleToggleEnabled = async (id: string, currentlyEnabled: boolean) => {
    setActioningId(id);
    try {
      if (currentlyEnabled) {
        await AgentScheduleService.disable(id, token || undefined);
      } else {
        await AgentScheduleService.enable(id, token || undefined);
      }
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to toggle schedule');
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (id: string) => {
    const schedule = schedules.find((s) => s.id === id);
    if (!window.confirm(`Delete schedule "${schedule?.name || id}"? This cannot be undone.`)) return;
    setActioningId(id);
    try {
      await AgentScheduleService.delete(id, token || undefined);
      setSelectedScheduleId(null);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete schedule');
    } finally {
      setActioningId(null);
    }
  };

  const handleCreated = () => {
    loadData();
  };

  const openRunInAgents = useCallback((scheduleId: string, run: RunEntry) => {
    const params = new URLSearchParams({
      schedule: scheduleId,
      run: run.id,
    });
    window.open(`/agents?${params.toString()}`, '_blank');
  }, []);

  // Auth guard
  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0f1623]' : 'bg-gray-50'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-[#FFB52B]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-[#0f1623]' : 'bg-gray-50'}`}>
        <Login />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-[#0f1623]' : 'bg-gray-50'}`}>
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#FFB52B]/[0.04] blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-[#1a2332]/30 blur-[100px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="Go back to home"
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-[#FFB52B] flex-shrink-0" />
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">Agent Schedules</h1>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                Manage autonomous agent executions on cron, event triggers, and dependencies
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={loadData}
              disabled={loading}
              className="p-2.5 sm:p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
              title="Refresh"
              aria-label="Refresh schedules"
            >
              <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={toggleDarkMode}
              className="p-2.5 sm:p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="h-4 w-4 text-gray-400" /> : <Moon className="h-4 w-4 text-gray-400" />}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-sm font-semibold
                bg-[#FFB52B] text-gray-900 hover:bg-[#FFB52B]/90
                shadow-lg shadow-[#FFB52B]/20 transition-all hover:shadow-[#FFB52B]/30"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New Schedule</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && schedules.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-[#FFB52B]" />
          </div>
        ) : schedules.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#FFB52B]/10 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-[#FFB52B]/60" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No schedules yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
              Create your first agent schedule to automate recurring AI tasks like metadata generation,
              transcript refinement, or YouTube sync.
            </p>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold
                bg-[#FFB52B] text-gray-900 hover:bg-[#FFB52B]/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create First Schedule
            </button>
          </div>
        ) : (
          /* Schedule grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {schedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                runs={runsMap[schedule.id] || []}
                onRunNow={handleRunNow}
                onStop={handleStop}
                onToggleEnabled={handleToggleEnabled}
                onDelete={handleDelete}
                onSelect={(s) => setSelectedScheduleId(s.id)}
                onClickRun={(run) => openRunInAgents(schedule.id, run)}
                isActioning={actioningId === schedule.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedSchedule && (
        <ScheduleDetail
          key={selectedSchedule.id}
          schedule={selectedSchedule}
          runs={runsMap[selectedSchedule.id] || []}
          token={token || ''}
          onClose={() => setSelectedScheduleId(null)}
          onRefresh={loadData}
          onRunNow={handleRunNow}
          onStop={handleStop}
          onToggleEnabled={handleToggleEnabled}
        />
      )}

      {/* Create modal */}
      <CreateScheduleModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
        token={token || ''}
        existingSchedules={schedules}
      />
    </div>
  );
};

export default SchedulesPage;
